import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import pluralize from "pluralize";

import { AD_LAYOUT_STUBS } from "@consts";
import { newsArticleSchema } from "@utils";
import { getTodayAnswers } from "@/common/services/spelllingBeeService";
import { getCMSCopy } from "@/common/services/cmsCopyService";

dayjs.extend(utc);
dayjs.extend(tz);

export default {
	data() {
		return {
			AD_LAYOUT_STUBS,
		};
	},
	async asyncData({ $axios, $config, res }) {
		const servicePromise = getTodayAnswers($axios, $config);
		const cmsPromise = getCMSCopy(
			$axios,
			$config,
			".seo.landing.nyt-spelling-bee",
			process.env.version
		);

		// Set cache expiration header:
		if (res) {
			const HOUR = 3;
			const MINUTE = 6;
			const now = dayjs().tz("America/New_York");
			let nextRefreshDate = now
				.hour(HOUR)
				.minute(MINUTE)
				.startOf("minute");
			if (nextRefreshDate.isBefore(now)) {
				// Same time tomorrow
				nextRefreshDate = nextRefreshDate.add(1, "day");
			}
			const expirationDate = nextRefreshDate
				.utc()
				.format("ddd, DD MMM YYYY HH:mm:ss [GMT]");
			res.setHeader("Expires", expirationDate);
		}

		const [serviceResponse, cmsResponse] = await Promise.all([
			servicePromise,
			cmsPromise,
		]);

		return {
			cmsContent: cmsResponse?.data || {},
			scores: serviceResponse?.data.scores || [],
			displayDate: serviceResponse?.data.displayDate || "",
			centerLetter: serviceResponse?.data.centerLetter || "",
			outerLetters: serviceResponse?.data.outerLetters || [],
			pangrams: serviceResponse?.data.pangrams || [],
			answers: serviceResponse?.data.answers || [],
		};
	},
	head() {
		let title = this.cmsContent.metaTitle;
		title = title.replace(/\{date\}/g, this.displayDate.split(",")[0]);
		return {
			title,
			meta: [
				{
					name: "description",
					hid: "description",
					content: this.cmsContent.metaDescription,
				},
			],
			script: [
				newsArticleSchema({
					headline: title,
					image: "https://wordfinder.yourdictionary.com/static/images/spelling-bee-og.jpg",
					datePublished: "2022-08-24T08:00:00.000Z",
				}),
			],
		};
	},
	computed: {
		letters() {
			const letters = [];
			if (this.centerLetter) {
				letters.push(this.centerLetter);
			}
			if (this.outerLetters?.length) {
				letters.push(...this.outerLetters);
			}

			return letters;
		},
		sections() {
			const s = [];
			if (this.pangrams.length) {
				s.push({
					title: this.$t("SpellingBeeTodayPangrams"),
					subtitle: pluralize("word", this.pangrams.length, true),
					words: this.pangrams,
				});
			}

			if (this.answers.length) {
				const grouped = this.answers.reduce((acc, word) => {
					const l = word.length;
					acc[l] = acc[l] || [];
					acc[l].push(word);
					return acc;
				}, {});

				Object.keys(grouped)
					.sort((a, b) => a - b)
					.forEach((length) => {
						const words = grouped[length];
						if (words.length) {
							s.push({
								title: `${length}-Letter Answers`,
								subtitle: pluralize("word", words.length, true),
								words,
							});
						}
					});
			}

			return s;
		},
		geniusPoints() {
			return this.scores?.[8] || 0;
		},
		totalPoints() {
			return this.scores?.[9] || 0;
		},
	},
};
